
import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { ActionButton } from '../Button';
import SelectField from '../SelectField';
import { ServicePriorityData, ServicePriorityEnum, ServiceStatusData, ServiceStatusEnum } from '../../data/Enums';
import Drawer, { IDefaultDrawerProps } from '../Drawer';
import useState from '../../hooks/useState';
import { useServiceOrderContext } from '../../contexts/ServiceOrderContext';
import { useAuthContext } from '../../contexts/AuthContext';
import DateField from '../DateField';
import Operators from '../../data/Operators';


interface IFilter {
    status: ServiceStatusEnum[];
    priority: ServicePriorityEnum[];
    department: number[];
    startDateOperator: string;
    startDate: Date | undefined;
    estimatedCompletionDateOperator: string;
    estimatedCompletionDate: Date | undefined;
}


const Filters: React.FC<IDefaultDrawerProps> = ({
    open,
    handleClose
}) => {
    const { translate } = useLanguageContext();
    const { enterprise, user } = useAuthContext();
    const { 
        selectedStatus,
        selectedPriority,
        selectedDepartment,
        startDateOperator, 
        startDate, 
        estimatedCompletionDateOperator,
        estimatedCompletionDate,
        handleChangeSelectedFilters 
    } = useServiceOrderContext();


    const state = useState<IFilter>({
        status: selectedStatus,
        priority: selectedPriority,
        department: selectedDepartment,
        startDateOperator: startDateOperator,
        startDate: startDate, 
        estimatedCompletionDateOperator: estimatedCompletionDateOperator,
        estimatedCompletionDate: estimatedCompletionDate, 
    });


    const _handleApplyFilters = useCallback((filters: IFilter) => {
        handleChangeSelectedFilters( 
            filters.status, 
            filters.priority,
            filters.department,
            filters.startDateOperator,
            filters.startDate,
            filters.estimatedCompletionDateOperator,
            filters.estimatedCompletionDate
        );
        handleClose();
        // eslint-disable-next-line
    }, []);


    const departmentsUser = useMemo(() => {
        return user?.enterprises.find(e => e.id === enterprise?.id)?.departmentsUser ?? [];
    }, [user, enterprise]);


    const footer = useMemo(() => {
        return <ActionButton
            action='apply'
            fullWidth={true}
            className='text-alwaysshow'
            onClick={() => _handleApplyFilters(state.values)}
        />;
        // eslint-disable-next-line
    }, [state.values, translate]);

    return (
        <Drawer
            anchor='right'
            open={open}
            handleClose={handleClose}
            title={translate('filter')}
            footer={footer}
        >
            <Grid container>
                <Grid item xs={12}>
                    <SelectField
                        label={translate('serviceStatus')}
                        name='status'
                        value={state.values.status.length > 0 ? state.values.status : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const status = state.values.status.length > 0 && values.includes(-1) ? [] : Object.values(ServiceStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, status);
                        }}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(ServiceStatusData).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('priority')}
                        name='priority'
                        value={state.values.priority.length > 0 ? state.values.priority : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const priority = state.values.priority.length > 0 && values.includes(-1) ? [] : Object.values(ServicePriorityData).filter(s => values.includes(s.key)).map(s => s.key);
                            state.update(name, priority);
                        }}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(Object.values(ServicePriorityData).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('departments')}
                        name='department'
                        value={state.values.department.length > 0 ? state.values.department : [-1]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const department = state.values.department.length > 0 && values.includes(-1) ? [] : values.filter(v => v !== -1);
                            state.update(name, department);
                        }}
                        multiple={true}
                        data={[{ value: -1, text: translate('all') }].concat(departmentsUser.map(d => {
                            return {
                                value: +d.id,
                                text: d.name
                            };
                        }))}
                    />
                </Grid>
                
                <Grid item xs={4}>
                    <SelectField
                        label={translate('operator')}
                        name='startDateOperator'
                        value={state.values.startDateOperator}
                        required={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Operators}
                    />
                </Grid>

                <Grid item xs={8}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={state.values.startDate}
                        onChange={value => state.update('startDate', value)}
                        required={false}
                    />
                </Grid>
                
                <Grid item xs={4}>
                    <SelectField
                        label={translate('operator')}
                        name='estimatedCompletionDateOperator'
                        value={state.values.estimatedCompletionDateOperator}
                        required={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Operators}
                    />
                </Grid>

                <Grid item xs={8}>
                    <DateField
                        label={translate('estimatedCompletionDate')}
                        format='DD/MM/YYYY'
                        name='estimatedCompletionDate'
                        value={state.values.estimatedCompletionDate}
                        onChange={value => state.update('estimatedCompletionDate', value)}
                        required={false}
                    />
                </Grid>
            </Grid>
        </Drawer>
    );
}

export default Filters;